/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';

import { getMonthName } from '@futrli/shared-services';
import { TooltipContainer, UIDragListItem, UIDragListItemTypes } from '@futrli/ui-components';

import { ExtendedOrganisation } from '../../../../consolidation-context/types';
import { useConsolidationContext } from '../../../../consolidation-context/consolidation-context';
import styles from './organisation-drag-item.scss';
import Icon from 'carbon-react/lib/components/icon';
import Textbox from 'carbon-react/lib/components/textbox';

export interface OrganisationDragItemProps {
  disabled?: boolean;
  isEditDisabled?: boolean;
  isEditing?: boolean;
  onClick?: (id: string) => void;
  onDragStart?: (event: React.DragEvent, id: string) => void;
  onEditCode?: (orgId: string) => void;
  onSaveCode?: (newCode: string) => void;
  organisation: ExtendedOrganisation;
  tooltip?: ReactNode;
  type: UIDragListItemTypes;
}

export const OrganisationDragItem = ({
  disabled,
  isEditDisabled,
  isEditing,
  onClick,
  onDragStart,
  onEditCode,
  onSaveCode,
  organisation,
  tooltip,
  type
}: OrganisationDragItemProps) => {
  const {
    settings: { orgCodeMap }
  } = useConsolidationContext();

  const orgCode = orgCodeMap[organisation.id];
  const [codeInput, setCodeInput] = useState(orgCode || '???');

  useEffect(() => {
    if (isEditing) {
      setCodeInput(orgCode || '???');
    }
  }, [isEditing, orgCode]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => onSaveCode?.(e.target.value), [onSaveCode]);

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onSaveCode?.((e.target as HTMLInputElement).value);
      }
      if (e.key === 'Escape') {
        onSaveCode?.(orgCode);
      }
    },
    [onSaveCode, orgCode]
  );

  return (
    <UIDragListItem
      id={organisation.id}
      onClick={onClick}
      onDragStart={onDragStart}
      type={type}
      draggable={!disabled}
      dimmed={disabled}
    >
      <TooltipContainer tooltipMessage={tooltip} position="bottom" disableTooltip={!disabled}>
        <div className={styles.container}>
          <div className={styles.name}>{organisation.name}</div>
          <div className={styles.data}>
            {organisation.financial_year_end_month
              ? getMonthName(organisation.financial_year_end_month).substring(0, 3)
              : '???'}
          </div>
          <div className={styles.data}>{organisation.currency_code || '???'}</div>
          {type !== UIDragListItemTypes.ADD && (
            <div className={clsx(styles.data, styles.code)}>
              {isEditing ? (
                <Textbox
                  name="code"
                  autoFocus
                  size="small"
                  style={{ width: '63px', textTransform: 'uppercase' }}
                  value={codeInput}
                  onChange={e => setCodeInput(e.target.value)}
                  maxLength={3}
                  onBlur={handleBlur}
                  onKeyUp={handleKeyUp}
                />
              ) : (
                <button
                  onClick={() => onEditCode?.(organisation.id)}
                  className={clsx(styles.button, styles.edit)}
                  disabled={isEditDisabled}
                  data-testid="OrganisationDragItem-editCode"
                >
                  {orgCodeMap[organisation.id] || '???'}{' '}
                  <Icon type="edit" color="#c2cadc" className={clsx(styles.icon, styles.edit)} />
                </button>
              )}
            </div>
          )}
        </div>
      </TooltipContainer>
    </UIDragListItem>
  );
};
